
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';

export type Sweepstakes = {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  start_date: string;
  draw_date: string;
  status: string;
  winner_id: string | null;
  eligibility_criteria: {
    min_points: number;
    min_tier: string;
    activity_required: boolean;
    activity_days: number;
  };
  prize_details: {
    points: number;
    reward_id: string | null;
    description: string;
  };
  created_at: string;
  updated_at: string;
};

export type SweepstakesEntry = {
  id: string;
  sweepstakes_id: string;
  customer_id: string;
  entry_date: string;
  entry_source: string;
  customer_profiles?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    points_balance: number;
    tier: string;
  };
};

export const useSweepstakesData = () => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();
  const [sweepstakes, setSweepstakes] = useState<Sweepstakes[]>([]);
  const [rewards, setRewards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch sweepstakes on mount
  useEffect(() => {
    fetchSweepstakes();
    fetchRewards();
  }, []);

  const fetchSweepstakes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('sweepstakes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Safely parse JSON fields
      const parsedData = data?.map(item => {
        // Ensure eligibility_criteria has the correct shape
        let eligibilityCriteria = typeof item.eligibility_criteria === 'string' 
          ? JSON.parse(item.eligibility_criteria) 
          : item.eligibility_criteria;
          
        // Ensure the object has all required properties with default values if needed
        eligibilityCriteria = {
          min_points: eligibilityCriteria?.min_points ?? 0,
          min_tier: eligibilityCriteria?.min_tier ?? 'standard',
          activity_required: eligibilityCriteria?.activity_required ?? false,
          activity_days: eligibilityCriteria?.activity_days ?? 30
        };
        
        // Do the same for prize_details
        let prizeDetails = typeof item.prize_details === 'string' 
          ? JSON.parse(item.prize_details) 
          : item.prize_details;
          
        prizeDetails = {
          points: prizeDetails?.points ?? 0,
          reward_id: prizeDetails?.reward_id ?? null,
          description: prizeDetails?.description ?? ''
        };
        
        return {
          ...item,
          eligibility_criteria: eligibilityCriteria,
          prize_details: prizeDetails
        } as Sweepstakes;
      }) || [];
      
      setSweepstakes(parsedData);
    } catch (error) {
      console.error('Error fetching sweepstakes:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorFetchingSweepstakes', 'Error fetching sweepstakes data'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRewards = async () => {
    try {
      const { data, error } = await supabase
        .from('rewards')
        .select('id, name')
        .eq('is_active', true);
      
      if (error) throw error;
      setRewards(data || []);
    } catch (error) {
      console.error('Error fetching rewards:', error);
    }
  };

  return {
    sweepstakes,
    loading,
    rewards,
    fetchSweepstakes
  };
};
